import { Button, Loader } from "@mantine/core";
import { IconAlertTriangle, IconMessage2 } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { predictionApi } from "#/api/predictionApi";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import { EmptyState } from "#/components/ui/EmptyState";
import InfoTooltip from "#/components/ui/InfoTooltip";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { UserRole } from "#/types/user";
import { formatDate } from "#/utils/dateTimeUtils";
import { isPastShipment } from "./predictionUtils";
import FeedbackModal from "./components/FeedbackModal";
import ShipmentOutcomeCard from "./components/ShipmentOutcomeCard";
import ShipmentOverview from "./components/ShipmentOverview";
import ShipmentTripsCard from "./components/ShipmentTripsCard";

/**
 * One shipment as its own page: the overview in plain sentences, the trips
 * (truck, driver, day, stops with their items and each trip's own road map),
 * and the data still missing. Every stop links to its prediction.
 */
const ShipmentDetailPage = () => {
  const t = usePrvTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const { client } = useClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === UserRole.ADMIN;
  const adminClientId = isAdmin ? Number(searchParams.get("clientId")) || undefined : undefined;
  const clientQuery = adminClientId ? `?clientId=${adminClientId}` : "";
  const shipmentId = Number(id);
  const now = useMemo(() => new Date(), []);
  const [feedbackOpened, setFeedbackOpened] = useState(false);

  const boardQuery = useQuery({
    queryKey: ["prediction-board", adminClientId ?? "own"],
    queryFn: async () => (await predictionApi.getBoard(adminClientId)).data,
    staleTime: 60_000,
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["shipment-detail", shipmentId],
    queryFn: async () => (await predictionApi.getShipment(shipmentId)).data,
    enabled: Number.isInteger(shipmentId) && shipmentId > 0,
    staleTime: 0,
  });

  if (isLoading || boardQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }
  if (isError || !data || !boardQuery.data) {
    return <EmptyState title={t("common.failedToProcess")} />;
  }
  const board = boardQuery.data;
  const { plan } = data;
  const past = isPastShipment(data, now);
  // Missing data is shown only when it changed this plan: a category without
  // specs (nothing can be sized), a truck without capacity when that left no
  // truck to plan with, and a driverless truck only when it actually drives.
  const missingShown = (plan?.missing ?? []).filter(
    (entry) => entry.kind === "category" || plan?.warnings.includes("missing_data")
  );
  const usedTruckIds = new Set((plan?.trips ?? []).map((trip) => trip.truck.vehicleId));
  const driverlessUsed = (plan?.trucks ?? []).filter(
    (truck) => truck.drivers.length === 0 && usedTruckIds.has(truck.vehicleId)
  );

  return (
    <DetailPageLayout
      title={
        <span className="truncate">
          {t("predictedDeliveries.shipment.pageTitle", {
            date: formatDate(data.shipment.shipmentDate, client),
            count: data.predictions.length,
          })}
        </span>
      }
      navRightSection={
        <Button
          size="xs"
          color="black"
          variant={data.shipment.feedbackUseful === null ? "filled" : "light"}
          leftSection={<IconMessage2 size={14} />}
          onClick={() => setFeedbackOpened(true)}
        >
          {t("predictedDeliveries.shipment.feedbackButton")}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 rounded-xl bg-slate-100 p-4">
        {/* A day gone by: what happened comes first; trucks and items of today are not shown. */}
        {past && <ShipmentOutcomeCard card={data} board={board} now={now} />}
        {!past && <ShipmentOverview card={data} settings={board.settings} showHow={isAdmin} />}

        {!past && plan && <ShipmentTripsCard card={data} board={board} showReasons={isAdmin} />}

        {!past && plan && (missingShown.length > 0 || driverlessUsed.length > 0) && (
          <section className="flex flex-col gap-3 rounded-lg border border-l-4 border-orange-300 border-l-orange-500 bg-orange-50 p-4 shadow-sm">
            <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-orange-800 uppercase">
              <IconAlertTriangle size={14} />
              {t("predictedDeliveries.shipment.gaps.title")}
              <InfoTooltip content={t("predictedDeliveries.shipment.gaps.tip")} size="sm" />
            </h4>
            <ul className="list-disc pl-5 text-sm text-orange-900">
              {missingShown.map((entry) => (
                <li key={`${entry.kind}-${entry.id}`}>
                  {t(`predictedDeliveries.truck.detail.missing.${entry.kind}`, {
                    name: entry.name,
                    fields: entry.fields
                      .map((field) => t(`predictedDeliveries.truck.detail.fields.${field}`))
                      .join(", "),
                  })}
                </li>
              ))}
              {driverlessUsed.map((truck) => (
                <li key={`driver-${truck.vehicleId}`}>
                  {t("predictedDeliveries.shipment.gaps.noDrivers", { name: truck.name })}
                </li>
              ))}
            </ul>
            <Button
              variant="subtle"
              size="xs"
              className="self-start"
              onClick={() => void navigate(`/data-health${clientQuery}`)}
            >
              {t("predictedDeliveries.shipment.gaps.openDataHealth")}
            </Button>
          </section>
        )}
      </div>
      <FeedbackModal
        shipment={data.shipment}
        opened={feedbackOpened}
        onClose={() => setFeedbackOpened(false)}
      />
    </DetailPageLayout>
  );
};

export default ShipmentDetailPage;
